import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { StagingBadge } from '../common/StagingBadge';
// import { ChatWidget } from '../common/ChatWidget';
// import { isFeatureEnabled } from '../../config/features';
import { useToast } from '../common/Toast';
import { useAuthStore } from '../../store/authStore';
import { cn } from '../../utils/cn';
import { authService } from '../../services/authService';
import { useLanguage } from '../../features/language/useLanguage';
import { useNotificationStream } from '../../hooks/useNotificationStream';
import { SubscriptionBanner } from '../common/SubscriptionBanner';
import { ChatWidget as SupportChatWidget } from '../../features/chat-support/ChatWidget';
import { OnboardingOverlay } from '../onboarding/OnboardingOverlay';
import { useOnboardingStore } from '../../store/onboardingStore';
import { onboardingService } from '../../services/onboardingService';
import { isOnboardingNavPhase } from '../onboarding/onboardingSteps';

export const DashboardLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  // const [chatOpen, setChatOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const { t } = useLanguage();
  const logout = useAuthStore((state) => state.logout);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const syncProfile = useAuthStore((state) => state.syncProfile);
  const token = useAuthStore((state) => state.token);

  useNotificationStream(token);

  const hydrateOnboarding = useOnboardingStore((s) => s.hydrate);
  const onboardingStatus = useOnboardingStore((s) => s.state?.status);
  const onboardingStep = useOnboardingStore((s) => s.state?.currentStep);
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    void (async () => {
      try {
        await hydrateOnboarding();
        if (cancelled) return;
        const st = useOnboardingStore.getState().state;
        // Auto-start only for someone who has genuinely never been here. This one
        // line is what turned a single bad read of onboarding_state into a
        // relaunched walkthrough: the tour is kicked off from `not_started` alone,
        // so anything that made the server report not_started put a user who had
        // already finished back on step 1 with the welcome card. (It did happen —
        // see decodeOnboardingState in the Go repository.) hasOnboardedBefore is
        // the fact that survives a replay, and a returning user already has
        // "Replay tour" in the header, so they never need this path.
        if (st?.status === 'not_started' && !st.hasOnboardedBefore) {
          const started = await onboardingService.start();
          if (!cancelled) useOnboardingStore.getState().setState(started);
        }
      } catch {
        /* onboarding is best-effort; never block the dashboard */
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated, hydrateOnboarding]);

  // The tour points at a sidebar menu item to reach each step's screen. On
  // mobile the sidebar is an off-canvas drawer (closed by default), so that
  // target would sit off-screen and the tour could never advance — and the
  // overlay's scrim blocks the hamburger, so the user can't open it by hand.
  // Open the drawer for the duration of the nav phase; clicking a menu item
  // navigates and closes it again (Sidebar's NavLink onClick → onClose). Setting
  // it on desktop is harmless (the sidebar ignores `isOpen` via md:translate-x-0).
  useEffect(() => {
    if (onboardingStatus !== 'in_progress') return;
    if (isOnboardingNavPhase(onboardingStep, location.pathname)) {
      // Intentional: force the drawer open on entering a nav phase so the tour
      // target is reachable. Guarded by the phase check; React bails out when
      // it's already open, so this can't cascade.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSidebarOpen(true);
    }
  }, [onboardingStatus, onboardingStep, location.pathname]);

  useEffect(() => {
    const handleUnauthorized = () => {
      showToast(t('dashboard.sessionExpired'), 'error');
      logout();
      navigate('/login');
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, [navigate, showToast, logout, t]);

  useEffect(() => {
    const handleUpgradeRequired = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const msg: string =
        detail?.message ?? 'Pro subscription required for this action.';
      showToast(msg, 'warning');
    };

    window.addEventListener('subscription:upgrade-required', handleUpgradeRequired);
    return () => window.removeEventListener('subscription:upgrade-required', handleUpgradeRequired);
  }, [showToast]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    let isCancelled = false;

    const syncAuthProfile = async () => {
      try {
        const response = await authService.getMe();
        const backendUser = response?.user;
        const backendCompany = response?.company;

        if (isCancelled || !backendUser || !backendCompany) {
          return;
        }

        syncProfile(
          {
            id: backendUser.id,
            email: backendUser.email,
            name: backendUser.full_name,
            role: backendUser.role,
            companyId: backendUser.company_id,
            emailNotificationsEnabled: backendUser.email_notifications_enabled ?? false,
          },
          {
            id: backendCompany.id,
            name: backendCompany.name,
            code: backendCompany.code,
            address: backendCompany.address,
            email: backendCompany.email,
            phone: backendCompany.phone,
            website: backendCompany.website,
            currency_symbol: backendCompany.currency_symbol,
          },
          response?.permissions,
          response?.company_can_edit_roles
        );
      } catch {
        if (!isCancelled) {
          logout();
          navigate('/login', { replace: true });
        }
      }
    };

    void syncAuthProfile();

    return () => {
      isCancelled = true;
    };
  }, [isAuthenticated, logout, navigate, syncProfile]);

  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden" data-test-id="dashboard-layout">
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />
      <div className={cn("flex-1 flex flex-col overflow-hidden min-h-0 min-w-0 transition-all duration-300", isSidebarCollapsed ? "md:ml-20" : "md:ml-64")}>
        <Header onMenuClick={() => setSidebarOpen(true)} />
        <SubscriptionBanner />
        <main className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 p-4 sm:p-6">
          <Outlet />
        </main>
      </div>

      {/* ChatWidget removed/disabled in favor of AIAssistantWelcome in DashboardPage
      {isFeatureEnabled('AI_ASSISTANT') && (
        <ChatWidget
          isOpen={chatOpen}
          onClose={() => setChatOpen(false)}
          onToggle={() => setChatOpen(!chatOpen)}
        />
      )}
      */}
      <SupportChatWidget />
      <OnboardingOverlay />
      <StagingBadge />
    </div>
  );
};
